/**
 * Admin sidebar — vanilla TS replacement for the inline `<script>` block
 * previously embedded in `_admin_sidebar_base.html.erb`.
 *
 * Event delegation is bound on `[data-sidebar]` so dynamically rendered rows
 * stay live; intercom + modal-blur triggers delegate from `document` so they
 * keep working when their host modals are appended after page load.
 */

declare global {
  interface Window {
    __adminSidebarInitialised?: boolean
    __modal?: {
      show: (target: HTMLElement | string) => void
      hide: (target: HTMLElement | string) => void
      toggle: (target: HTMLElement | string) => void
    }
  }
}

const SIDEBAR_SELECTOR = "[data-sidebar]"
const LINK_SELECTOR = "[data-sidebar-link]"
const SUBMENU_SELECTOR = "[data-sidebar-submenu]"
const ACTION_SELECTOR = "[data-sidebar-action]"
const INTERCOM_TRIGGER_SELECTOR = ".js-trigger-intercom"
const BLUR_TARGETS = ["sidebar", "header", "page"]

if (!window.__adminSidebarInitialised) {
  window.__adminSidebarInitialised = true

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init)
  } else {
    init()
  }
}

function init(): void {
  document
    .querySelectorAll<HTMLElement>(SIDEBAR_SELECTOR)
    .forEach(bindSidebar)

  bindModalBlur()
  bindIntercomTriggers()
}

function bindSidebar(sidebar: HTMLElement): void {
  sidebar.addEventListener("click", (event) => handleClick(event, "_self"))
  sidebar.addEventListener("auxclick", (event) => {
    if (event.button !== 1) return
    handleClick(event, "_blank")
  })
}

function handleClick(event: MouseEvent, target: "_self" | "_blank"): void {
  const origin = event.target
  if (!(origin instanceof Element)) return

  const action = origin.closest<HTMLElement>(ACTION_SELECTOR)
  if (action) {
    event.stopPropagation()
    onActionClick(action)
    return
  }

  const submenu = origin.closest<HTMLElement>(SUBMENU_SELECTOR)
  if (submenu) {
    onSubmenuClick(submenu)
    return
  }

  const link = origin.closest<HTMLElement>(LINK_SELECTOR)
  if (link) {
    onLinkClick(link, target)
  }
}

function trackEvent(element: HTMLElement): void {
  const name = element.dataset.event
  if (!name) return

  const raw = element.dataset.eventOptions
  let options: unknown = undefined
  if (raw) {
    try {
      options = JSON.parse(raw)
    } catch {
      // Malformed payload — fire the event without options rather than break delegation.
    }
  }
  window.segmentTrackEvent?.(name, options)
}

function isInteractive(element: HTMLElement): boolean {
  return (
    element.dataset.disabled !== "true" &&
    element.dataset.modalToggle === undefined
  )
}

function onLinkClick(element: HTMLElement, target: "_self" | "_blank"): void {
  trackEvent(element)
  if (!isInteractive(element)) return

  const url = element.dataset.link
  if (url) window.open(url, target)
}

function onSubmenuClick(element: HTMLElement): void {
  trackEvent(element)
  if (!isInteractive(element)) return

  const value = element.dataset.value
  if (!value) return

  const target = document.querySelector<HTMLElement>(
    `[data-submenu="${value}"]`,
  )
  if (!target) return

  const opened = document.querySelector<HTMLElement>(
    `[data-submenu][data-open="true"]`,
  )
  if (opened && opened !== target) {
    opened.setAttribute("data-open", "false")
  }

  const isOpen = target.dataset.open === "true"
  target.setAttribute("data-open", isOpen ? "false" : "true")
}

function onActionClick(element: HTMLElement): void {
  trackEvent(element)

  const url = element.dataset.url
  if (url) window.open(url, "_blank")
}

function bindModalBlur(): void {
  // Listens for native CustomEvents dispatched by modal.ts (via __modal). Modals
  // opened with jQuery `.modal('show')` won't fire this — route through __modal.
  document.addEventListener("show.bs.modal", (event) => {
    if (matchesBlurTrigger(event.target)) setBlur(true)
  })
  document.addEventListener("hide.bs.modal", (event) => {
    if (matchesBlurTrigger(event.target)) setBlur(false)
  })
}

function matchesBlurTrigger(target: EventTarget | null): boolean {
  return target instanceof Element && target.matches(".js-add-blur-background")
}

function setBlur(on: boolean): void {
  const value = on ? "blur(3px)" : ""
  BLUR_TARGETS.forEach((id) => {
    const el = document.getElementById(id)
    if (el) el.style.filter = value
  })
}

function bindIntercomTriggers(): void {
  document.addEventListener("click", (event) => {
    const origin = event.target
    if (!(origin instanceof Element)) return

    const trigger = origin.closest(INTERCOM_TRIGGER_SELECTOR)
    if (!trigger) return

    window.zE?.("messenger", "open")
    window.__modal?.hide(".center-marketing-modal")
  })
}

export {}
